import { ChangeDetectionStrategy, Component, Injector, ViewEncapsulation } from '@angular/core';
import { ItemsListRowItemBase } from '@fi-app/components/items-list/list-row/list-row-item/list-row-item-base';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { InvoiceService } from '@fi-app/customer-billing/customer-billing-invoice/invoice.service';
import { FiInfoIconMode } from '@fi-app/shared/info-icon/info-icon.component';
import {
    CustomerBillingInvoiceModel,
    ICustomerBillingInvoiceStatusData,
} from '@fi-app/customer-billing/customer-billing-invoice/customer-billing-invoice.model';
import { InvoiceStatusType } from '@fi-app/customer-billing/customer-billing-invoice/invoice-type';
import { takeUntil } from 'rxjs/operators';
import { FiDateTimeSecFormatOptions } from '@fi-shared/utils';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { get } from 'jquery';

@Component({
    selector: 'fi-invoice-status-list-item',
    templateUrl: './invoice-status-list-item.component.html',
    styleUrls: ['./invoice-status-list-item.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-invoice-status-list-item' },
    standalone: false,
})
export class InvoiceStatusListItemComponent extends ItemsListRowItemBase {
    get invoice(): CustomerBillingInvoiceModel {
        return this.data;
    }

    get columnStatusType(): InvoiceStatusType {
        return this.head.Properties.ExtraProperties.headData.columnStatusType;
    }

    private fiLoadingService: LoadingService;
    private invoiceService: InvoiceService;
    private fiFormatDateService: FiFormatDateService;
    FiInfoIconMode = FiInfoIconMode;

    constructor(injector: Injector) {
        super(injector);
        this.invoiceService = injector.get(InvoiceService);
        this.fiLoadingService = injector.get(LoadingService);
        this.fiFormatDateService = injector.get(FiFormatDateService);

        this.invoiceService.invoiceStatusChanged$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((invoiceId) => {
            if (this.invoice.Id === invoiceId) {
                this.cdr.detectChanges();
            }
        });
    }

    //# region texts and actions availability
    onInvoiceStatusClick($event: MouseEvent) {
        $event.stopPropagation();

        if (this.columnStatusType === InvoiceStatusType.Printed) {
            this.invoiceService.printInvoice(this.invoice, this.injector).then((result) => {
                this.invoice.PrintedDates.push(result);
                this.cdr.detectChanges();
            });
        }

        if (this.columnStatusType === InvoiceStatusType.Sent) {
            this.invoiceService.sendInvoice(this.invoice, this.injector).then((result) => {
                this.invoice.SentDates.push(result);
                this.cdr.detectChanges();
            });
        }

        if (this.columnStatusType === InvoiceStatusType.Paid) {
            if (this.isSwitchToCurrentStatusAvailable()) {
                this.processStatusActionPromise(this.invoiceService.payInvoice(this.invoice));
            }
            if (this.isSwitchToPrevStatusAvailable()) {
                this.processStatusActionPromise(this.invoiceService.cancelInvoicePaidState(this.invoice.Id));
            }
        }
    }

    private processStatusActionPromise(statusActionPromise: Promise<ICustomerBillingInvoiceStatusData>) {
        this.fiLoadingService.trackPromise(statusActionPromise).then((result) => {
            this.invoice.InvoiceStatusType = result.Status;
            //this.invoice.PrintedDate = result.PrintedDate;
            //this.invoice.SentDate = result.SentDate;
            this.invoice.PaidDate = result.PaidDate;
            this.invoiceService.triggerInvoiceStatusChanged(this.invoice.Id);
        });
    }

    //# endregion

    //# region texts and actions availability
    isSwitchToCurrentStatusAvailable() {
        if (this.columnStatusType === InvoiceStatusType.Printed || this.columnStatusType === InvoiceStatusType.Sent)
            return true;

        // paid column
        return this.invoice.InvoiceStatusType < this.columnStatusType;
    }

    isSwitchToPrevStatusAvailable() {
        if (this.columnStatusType === InvoiceStatusType.Printed || this.columnStatusType === InvoiceStatusType.Sent)
            return false;
        return this.invoice.InvoiceStatusType === this.columnStatusType;
    }

    isStatusFinished() {
        return (
            this.columnStatusType === InvoiceStatusType.Paid && this.invoice.InvoiceStatusType === this.columnStatusType
        );
    }

    getStatusActionText() {
        if (this.columnStatusType === InvoiceStatusType.Printed) {
            return this.invoice.PrintedDates.length
                ? 'billing.invoice.printed_status_printed'
                : 'billing.invoice.printed_status_not_printed';
        }
        if (this.columnStatusType === InvoiceStatusType.Sent) {
            return this.invoice.SentDates.length
                ? 'billing.invoice.sent_status_sent'
                : 'billing.invoice.sent_status_not_sent';
        }
        if (this.columnStatusType === InvoiceStatusType.Paid) {
            return this.invoice.InvoiceStatusType >= this.columnStatusType
                ? 'billing.invoice.pay_status_paid'
                : 'billing.invoice.pay_status_not_paid';
        }
    }

    getStatusTooltip() {
        if (this.columnStatusType === InvoiceStatusType.Printed) {
            return this.invoice.PrintedDates.length
                ? 'billing.invoice.printed_status_repeat_print_tooltip'
                : 'billing.invoice.printed_status_print_tooltip';
        }

        if (this.columnStatusType === InvoiceStatusType.Sent) {
            return this.invoice.SentDates.length
                ? 'billing.invoice.sent_status_repeat_sent_tooltip'
                : 'billing.invoice.sent_status_sent_tooltip';
        }

        if (this.columnStatusType === InvoiceStatusType.Paid) {
            if (this.isSwitchToCurrentStatusAvailable()) {
                return 'billing.invoice.payd_status_pay_tooltip';
            }
            if (this.isSwitchToPrevStatusAvailable()) {
                return 'billing.invoice.payd_status_cancel_pay_tooltip';
            }
        }
        return undefined;
    }

    getStatusDates(): string[] {
        const dates = [];
        if (this.columnStatusType === InvoiceStatusType.Printed) {
            dates.push(
                ...this.invoice.PrintedDates.map((x) =>
                    this.fiFormatDateService.formatDate(x.CreatedOn, FiDateTimeSecFormatOptions, { showToday: false }),
                ),
            );
        } else if (this.columnStatusType === InvoiceStatusType.Sent) {
            dates.push(
                ...this.invoice.SentDates.map((x) =>
                    this.fiFormatDateService.formatDate(x.CreatedOn, FiDateTimeSecFormatOptions, { showToday: false }),
                ),
            );
            return dates;
        } else if (this.columnStatusType === InvoiceStatusType.Paid) {
            if (this.invoice.PaidDate) {
                dates.push(
                    this.fiFormatDateService.formatDate(this.invoice.PaidDate, FiDateTimeSecFormatOptions, {
                        showToday: false,
                    }),
                );
            }
        }
        return dates;
    }

    //# endregion
}
